import { useState } from 'react';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

export interface TeamInGroup {
  teamId: number;
  name: string;
}

export interface GroupPicks {
  first: number | null;
  second: number | null;
  third: number | null;
}

interface Props {
  label: string;
  teams: TeamInGroup[];
  picks: GroupPicks;
  open: boolean;
  onToggle(): void;
  onChange(picks: GroupPicks): void;
  locked?: boolean;
}

export function GroupCard({ label, teams, picks, open, onToggle, onChange, locked }: Props) {
  const filled = (picks.first ? 1 : 0) + (picks.second ? 1 : 0) + (picks.third ? 1 : 0);

  function setSlot(slot: keyof GroupPicks, teamId: number) {
    haptic.select();
    const next: GroupPicks = { ...picks };
    // Clear this team from other slots first so a team isn't picked twice
    if (next.first === teamId) next.first = null;
    if (next.second === teamId) next.second = null;
    if (next.third === teamId) next.third = null;
    next[slot] = teamId;
    onChange(next);
  }

  return (
    <div className="rounded-xl border border-border-base bg-bg-card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex items-center justify-center w-9 h-9 rounded-md font-display text-xl',
              filled === 3 ? 'bg-trophy-gold text-bg-night' : 'bg-bg-darker text-text-faint border border-border-base',
            )}
          >
            {label}
          </span>
          <span className="font-chunky text-sm">Group {label}</span>
        </div>
        <span className="font-mono text-trophy-gold">{filled}/3</span>
      </button>

      {open && (
        <div className="border-t border-border-base px-4 py-3 space-y-2">
          {(['first', 'second', 'third'] as const).map((slot) => {
            const slotLabel = slot === 'first' ? '1st' : slot === 'second' ? '2nd' : '3rd (best-third)';
            return (
              <div key={slot}>
                <div className="font-display-alt text-[10px] uppercase tracking-widest text-text-faint mb-1">
                  {slotLabel}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {teams.map((t) => {
                    const selected = picks[slot] === t.teamId;
                    const usedElsewhere =
                      (picks.first === t.teamId && slot !== 'first') ||
                      (picks.second === t.teamId && slot !== 'second') ||
                      (picks.third === t.teamId && slot !== 'third');
                    return (
                      <button
                        key={t.teamId}
                        disabled={locked || usedElsewhere}
                        onClick={() => setSlot(slot, t.teamId)}
                        className={cn(
                          'rounded-md px-2 py-2 text-xs font-semibold border transition-colors text-left',
                          selected
                            ? 'bg-trophy-gold text-bg-night border-trophy-gold'
                            : usedElsewhere
                              ? 'bg-bg-darker text-text-faint border-border-base opacity-40'
                              : 'bg-bg-elevated text-text-base border-border-base',
                        )}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface CollapsibleProps {
  children: React.ReactNode;
}
export function GroupAccordion({ children }: CollapsibleProps) {
  return <div className="space-y-2">{children}</div>;
}

interface UseGroupOpenStateProps {
  initialOpen?: string;
}

export function useGroupOpenState({ initialOpen }: UseGroupOpenStateProps = {}) {
  const [openLabel, setOpenLabel] = useState<string | null>(initialOpen ?? null);
  return {
    isOpen: (label: string) => openLabel === label,
    toggle: (label: string) => setOpenLabel((cur) => (cur === label ? null : label)),
  };
}
